// src/pages/admin/AdminSlider.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

const BUCKET = "slider";

export default function AdminSlider() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(null); // success | error | info

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [sort, setSort] = useState("");

  const styles = useMemo(
    () => ({
      wrap: { maxWidth: 960, margin: "24px auto", padding: "0 16px" },
      h2: { fontSize: 22, fontWeight: 800, margin: "0 0 12px" },
      card: {
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        padding: 16,
      },
      grid: { display: "grid", gap: 12 },
      row: { display: "grid", gap: 6 },
      two: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" },
      label: { fontSize: 13, color: "#374151", fontWeight: 600 },
      input: {
        height: 40,
        border: "1px solid #d1d5db",
        borderRadius: 10,
        padding: "0 10px",
        outline: "none",
      },
      btn: {
        height: 40,
        padding: "0 14px",
        borderRadius: 10,
        border: "1px solid #0ea5e9",
        background: "#0ea5e9",
        color: "#fff",
        fontWeight: 700,
        cursor: "pointer",
      },
      tableWrap: {
        marginTop: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflowX: "auto",
      },
      table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
      th: {
        textAlign: "left",
        padding: "10px 12px",
        background: "#f8fafc",
        borderBottom: "1px solid #e5e7eb",
        whiteSpace: "nowrap",
      },
      td: {
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        verticalAlign: "middle",
      },
      thumb: {
        width: 160,
        height: 90,
        objectFit: "cover",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
      },
      tagOn: {
        padding: "2px 6px",
        borderRadius: 999,
        background: "#dcfce7",
        border: "1px solid #bbf7d0",
        color: "#166534",
        fontSize: 12,
        fontWeight: 700,
      },
      tagOff: {
        padding: "2px 6px",
        borderRadius: 999,
        background: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#991b1b",
        fontSize: 12,
        fontWeight: 700,
      },
      msg: (t) => ({
        marginTop: 8,
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid",
        ...(t === "success"
          ? { background: "#ecfdf5", borderColor: "#6ee7b7", color: "#065f46" }
          : t === "error"
          ? { background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }
          : {
              background: "#f1f5f9",
              borderColor: "#e2e8f0",
              color: "#334155",
            }),
      }),
      rowActions: { display: "flex", gap: 8, alignItems: "center" },
      btnGrey: {
        height: 32,
        padding: "0 10px",
        borderRadius: 8,
        border: "1px solid #64748b",
        background: "#64748b",
        color: "#fff",
        cursor: "pointer",
      },
      btnRed: {
        height: 32,
        padding: "0 10px",
        borderRadius: 8,
        border: "1px solid #ef4444",
        background: "#ef4444",
        color: "#fff",
        cursor: "pointer",
      },
    }),
    []
  );

  const fetchRows = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("slider_images")
      .select("*")
      .order("sort_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });
    if (error) {
      console.error(error);
      setMsg("讀取失敗");
      setMsgType("error");
    } else {
      setRows(data || []);
      setMsg("");
      setMsgType(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const uploadToBucket = async (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filename = `s_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}.${ext}`;
    const up = await supabase.storage.from(BUCKET).upload(filename, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });
    if (up.error) throw up.error;
    const pub = supabase.storage.from(BUCKET).getPublicUrl(filename);
    return pub.data.publicUrl;
  };

  const createRow = async () => {
    if (!file) {
      setMsg("請選擇一張圖片上傳");
      setMsgType("info");
      return;
    }
    try {
      setLoading(true);
      const image_url = await uploadToBucket(file);
      const payload = {
        image_url,
        title: title.trim() || null,
        link_url: link.trim() || null,
        sort_order: String(sort).trim() === "" ? null : Number(sort) || null,
        is_active: true,
      };
      const { error } = await supabase.from("slider_images").insert(payload);
      if (error) throw error;
      setMsg("✅ 已新增輪播圖片");
      setMsgType("success");
      setFile(null);
      setTitle("");
      setLink("");
      setSort("");
      fetchRows();
    } catch (e) {
      console.error(e);
      setMsg("新增失敗");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (row) => {
    const { error } = await supabase
      .from("slider_images")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) return console.error(error);
    setRows((rs) =>
      rs.map((r) => (r.id === row.id ? { ...r, is_active: !r.is_active } : r))
    );
  };

  const updateSort = async (row, v) => {
    const value = String(v).trim() === "" ? null : Number(v) || null;
    const { error } = await supabase
      .from("slider_images")
      .update({ sort_order: value })
      .eq("id", row.id);
    if (error) return console.error(error);
    setRows((rs) =>
      rs.map((r) => (r.id === row.id ? { ...r, sort_order: value } : r))
    );
  };

  const remove = async (row) => {
    if (!window.confirm("確定刪除此圖片？")) return;
    try {
      setLoading(true);
      const { error } = await supabase
        .from("slider_images")
        .delete()
        .eq("id", row.id);
      if (error) throw error;

      // 同步刪 Storage 檔（可選）
      if (row.image_url) {
        const prefix = `/object/public/${BUCKET}/`;
        const idx = row.image_url.indexOf(prefix);
        if (idx > -1) {
          const path = row.image_url.slice(idx + prefix.length);
          await supabase.storage.from(BUCKET).remove([path]);
        }
      }

      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setMsg("✅ 已刪除");
      setMsgType("success");
    } catch (e) {
      console.error(e);
      setMsg("刪除失敗");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={styles.wrap}>
      <h2 style={styles.h2}>首頁輪播圖管理</h2>

      {/* 新增區塊 */}
      <div style={styles.card}>
        <div style={styles.grid}>
          <div style={styles.row}>
            <label style={styles.label}>圖片（建議 1600×900 以上）</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={loading}
            />
          </div>
          <div style={styles.two}>
            <div style={styles.row}>
              <label style={styles.label}>標題（可選）</label>
              <input
                style={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例：門診公告"
                disabled={loading}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>點擊連結（可選）</label>
              <input
                style={styles.input}
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
                disabled={loading}
              />
            </div>
          </div>
          <div style={styles.row}>
            <label style={styles.label}>排序（數字小在前，可空）</label>
            <input
              style={styles.input}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              placeholder="10、20..."
              disabled={loading}
            />
          </div>
          <div>
            <button style={styles.btn} onClick={createRow} disabled={loading}>
              {loading ? "處理中…" : "新增圖片"}
            </button>
          </div>
          {msg && <div style={styles.msg(msgType)}>{msg}</div>}
        </div>
      </div>

      {/* 列表 */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>預覽</th>
              <th style={styles.th}>標題</th>
              <th style={styles.th}>連結</th>
              <th style={styles.th}>排序</th>
              <th style={styles.th}>狀態</th>
              <th style={styles.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 16, color: "#6b7280" }}>
                  {loading ? "讀取中…" : "尚無圖片"}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>
                    <img
                      src={r.image_url}
                      alt={r.title || "slider"}
                      style={styles.thumb}
                      onError={(e) =>
                        (e.currentTarget.src = "/images/work1.jpg")
                      }
                    />
                  </td>
                  <td style={styles.td}>{r.title || "—"}</td>
                  <td style={styles.td}>
                    {r.link_url ? (
                      <a href={r.link_url} target="_blank" rel="noreferrer">
                        {r.link_url}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={styles.td}>
                    <input
                      style={{ ...styles.input, width: 100, height: 32 }}
                      defaultValue={r.sort_order ?? ""}
                      onBlur={(e) => updateSort(r, e.target.value)}
                    />
                  </td>
                  <td style={styles.td}>
                    <span style={r.is_active ? styles.tagOn : styles.tagOff}>
                      {r.is_active ? "顯示中" : "已隱藏"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.rowActions}>
                      <button
                        style={styles.btnGrey}
                        onClick={() => toggleActive(r)}
                      >
                        {r.is_active ? "設為隱藏" : "設為顯示"}
                      </button>
                      <button style={styles.btnRed} onClick={() => remove(r)}>
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
